export type MascotHoliday =
  | "new-year"
  | "lunar-new-year"
  | "spring-holiday"
  | "halloween"
  | "winter-holiday";

// Lunisolar dates follow no closed form, so they are tabled; past the last
// year the fireworks stop appearing.
const LUNAR_NEW_YEAR = new Map<number, [month: number, day: number]>([
  [2026, [2, 17]],
  [2027, [2, 6]],
  [2028, [1, 26]],
  [2029, [2, 13]],
  [2030, [2, 3]],
  [2031, [1, 23]],
  [2032, [2, 11]],
  [2033, [1, 31]],
  [2034, [2, 19]],
  [2035, [2, 8]],
  [2036, [1, 28]],
]);

/** Anonymous Gregorian algorithm, e.g. 2027 → March 28. */
function springHolidayDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** The holiday on `now`'s local date, e.g. Oct 31 → `"halloween"`. */
export function mascotHolidayOn(now: Date): MascotHoliday | null {
  const year = now.getFullYear();
  const holidays: [MascotHoliday, Date][] = [
    ["new-year", new Date(year, 0, 1)],
    ["spring-holiday", springHolidayDate(year)],
    ["halloween", new Date(year, 9, 31)],
    ["winter-holiday", new Date(year, 11, 25)],
  ];
  const lunarNewYear = LUNAR_NEW_YEAR.get(year);
  if (lunarNewYear) {
    const [month, day] = lunarNewYear;
    holidays.push(["lunar-new-year", new Date(year, month - 1, day)]);
  }
  const today = holidays.find(
    ([, date]) =>
      date.getMonth() === now.getMonth() && date.getDate() === now.getDate(),
  );
  return today?.[0] ?? null;
}

/**
 * Whether any time zone is on a holiday at `now`. Local dates run from a day
 * behind UTC's to a day ahead, so this needs no browser.
 */
export function mascotHolidayAnywhere(now: Date): boolean {
  return [-1, 0, 1].some((offset) => {
    const date = new Date(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + offset,
    );
    return mascotHolidayOn(date) !== null;
  });
}

/**
 * Holiday mascots have one pose and ignore typing, e.g.
 * `("input", "dark", "spring-holiday")` → `"anvil-spring-holiday-dark.png"`.
 */
export function newSessionMascotPath(
  pose: "hello" | "input",
  theme: "light" | "dark",
  holiday: MascotHoliday | null,
): string {
  return holiday
    ? `anvil-${holiday}-${theme}.png`
    : `anvil-${pose}-${theme}.png`;
}
